'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { BaseModal } from '@components/base-modal';
import { ACTIVE_EXPERIMENTS } from '@config/experiments';
import { clearAllExperimentOverrides, clearExperimentOverride, getExperimentOverride, setExperimentOverride } from '@utils/experiment-bucketing';

/**
 * Developer panel for forcing experiment variants.
 *
 * Bucketing is deterministic on an anonymous cookie, so without this there is no way to
 * see a variant you were not assigned — which makes an unreleased feature behind a 0%
 * variant impossible to look at. Each row forces one experiment for this browser only.
 *
 * Mounted by `ExperimentsProvider`, so it is available on every page that has experiments
 * at all. Opened with the hotkey below.
 */

/** Sentinel for "no override" — distinct from any real variant value. */
const AUTO = '__auto__';
type Props = {
  isOpen: boolean;
  onClose: () => void;
};
export const ExperimentsPanel: React.FC<Props> = t0 => {
  const $ = _c(24);
  const {
    isOpen,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [overrides, setOverrides] = React.useState(t1);
  let t2;
  let t3;
  if ($[1] !== isOpen) {
    t2 = () => {
      if (!isOpen) {
        return;
      }
      const current = {};
      ACTIVE_EXPERIMENTS.forEach(experiment => {
        const override = getExperimentOverride(undefined, experiment.id);
        if (override) {
          current[experiment.id] = override;
        }
      });
      setOverrides(current);
    };
    t3 = [isOpen];
    $[1] = isOpen;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  React.useEffect(t2, t3);
  const applyAndReload = _temp;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (experimentId, value) => {
      if (value === AUTO) {
        clearExperimentOverride(experimentId);
      } else {
        setExperimentOverride(experimentId, value);
      }
      applyAndReload();
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleChange = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      clearAllExperimentOverrides();
      applyAndReload();
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleResetAll = t5;
  let t6;
  if ($[6] !== overrides) {
    t6 = Object.keys(overrides);
    $[6] = overrides;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const hasOverrides = t6.length > 0;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div><h2 className="text-lg font-semibold text-primary-main">Experiment overrides</h2><p className="mt-1 text-[13px] text-primary-main/60">Forces variants for this browser only — nobody else is affected. Cleared when you close the browser.</p></div>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== hasOverrides) {
    t8 = hasOverrides && <button type="button" onClick={handleResetAll} className="shrink-0 rounded-full border-[1.5px] border-primary-main px-4 py-1.5 text-sm text-primary-main hover:border-primary-dark">Reset all</button>;
    $[9] = hasOverrides;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <div className="flex items-start justify-between gap-4 pr-10">{t7}{t8}</div>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== overrides) {
    t10 = ACTIVE_EXPERIMENTS.map(experiment_0 => {
      const override_0 = overrides[experiment_0.id];
      return <div key={experiment_0.id} className="rounded-xl border border-gray-light p-4"><div className="flex items-center gap-2"><p className="text-sm font-medium text-primary-main">{experiment_0.name}</p>{override_0 && <span className="rounded-full bg-alert-main/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-main">Overridden</span>}</div><p className="mt-0.5 font-mono text-[12px] text-primary-main/50">{experiment_0.id}</p><div className="mt-3 flex flex-wrap gap-2">{[{
            value: AUTO,
            label: "Auto (bucketed)"
          }, ...experiment_0.variants.map(_temp2)].map(option => {
            const isSelected = (override_0 ?? AUTO) === option.value;
            return <button key={option.value} type="button" aria-pressed={isSelected} onClick={() => handleChange(experiment_0.id, option.value)} className={`rounded-full px-3 py-1.5 text-[13px] transition-colors ${isSelected ? "bg-primary-main text-white" : "bg-gray-xlight text-primary-main hover:bg-gray-light"}`}>{option.label}</button>;
          })}</div></div>;
    });
    $[13] = overrides;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10) {
    t11 = <div className="mt-6 space-y-5">{t10}</div>;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t9) {
    t12 = <div className="p-6">{t9}{t11}</div>;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isOpen || $[21] !== onClose || $[22] !== t12) {
    t13 = <BaseModal isOpen={isOpen} onClose={onClose} size="lg" ariaLabel="Experiment overrides">{t12}</BaseModal>;
    $[20] = isOpen;
    $[21] = onClose;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
};
function _temp() {
  return window.location.reload();
}
function _temp2(variant) {
  return {
    value: variant.value,
    label: `${variant.value} · ${variant.percentage}%`
  };
}